import type {
  FieldOption,
  Panel,
  ReportTemplate,
  Theme,
} from './report-typing';

import a19ReportLogo from '@src/assets/images/A19ReportLogo.png';

export const defaultSelections: Record<string, FieldOption[]> = {
  patientBiologicalSex: [
    { id: 'male', label: 'Male' },
    { id: 'female', label: 'Female' },
    { id: 'other', label: 'Other' },
  ],
  dataComplete: [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
  ],
  imageQuality: [
    { id: 'excellent', label: 'Excellent' },
    { id: 'good', label: 'Good' },
    {
      id: 'adequate',
      label: 'Impaired but Diagnostic',
    },
    { id: 'poor', label: 'Non Diagnostic' },
  ],
};

const defaultSidebar: Panel = {
  panelId: 'sidebar',
  width: 0.25,
  padding: { top: 40, left: 20, right: 10, bottom: 0 },
  gap: 20,
  // background: '#ffffff',
  background: '#F4F4EF',
  sections: [
    {
      id: 'patient-section',
      kind: 'fieldGroup',
      title: 'Patient',
      rows: [
        [
          {
            key: 'patientName',
            type: 'text',
            label: 'Client',
            defaultValue: 'Client Name',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'PatientName',
              },
            },
          },
        ],
        [
          {
            key: 'patientID',
            type: 'text',
            label: 'Client ID',
            defaultValue: 'Client ID',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'PatientID',
              },
            },
          },
        ],
        [
          {
            key: 'patientBiologicalSex',
            type: 'dropdown',
            label: 'Biological Sex',
            defaultValue: '',
            options: defaultSelections.patientBiologicalSex,
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'PatientSex',
              },
            },
          },
        ],
        [
          {
            key: 'patientAge',
            type: 'number',
            label: 'Age at Report',
            defaultValue: '',
            isEditable: true,
            isRequired: false,
            isDisabled: false,
            relationship: {
              kind: 'derived',
              parentKeys: ['patientDateOfBirth', 'studyScanDate'],
              action: 'age',
              isAutomatic: true,
            },
          },
        ],
        [
          {
            key: 'patientDateOfBirth',
            type: 'date',
            label: 'Date of Birth',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'PatientBirthDate',
              },
            },
          },
        ],
      ],
    },
    {
      id: 'study-section',
      kind: 'fieldGroup',
      title: 'Study',
      rows: [
        [
          {
            key: 'studyProtocol',
            type: 'text',
            label: 'Protocol',
            defaultValue: 'OS-CMR',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
          },
        ],
        [
          {
            key: 'studyScanDate',
            type: 'date',
            label: 'Scan Date',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'StudyDate',
              },
            },
          },
        ],
        [
          {
            key: 'studyReportDate',
            type: 'date',
            label: 'Report Date',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'system',
                field: 'date',
              },
            },
          },
        ],
      ],
    },
    {
      id: 'data-section',
      kind: 'fieldGroup',
      title: 'Data',
      rows: [
        [
          {
            key: 'dataComplete',
            type: 'dropdown',
            label: 'Complete Data',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            options: defaultSelections.dataComplete,
          },
        ],
        [
          {
            key: 'imageQuality',
            type: 'dropdown',
            label: 'Image Quality',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            options: defaultSelections.imageQuality,
          },
        ],
      ],
    },
    {
      id: 'readers-section',
      kind: 'fieldGroup',
      title: 'Readers',
      rows: [
        [
          {
            key: 'reader1',
            type: 'dropdown',
            label: 'Reader 1',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            options: [],
            neighborKeys: ['reader2'],
            canAddAdditional: true,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'system',
                field: 'user',
              },
            },
          },
        ],
        [
          {
            key: 'reader2',
            type: 'dropdown',
            label: 'Reader 2',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            canAddAdditional: true,
            neighborKeys: ['reader1'],
            options: [],
          },
        ],
      ],
    },
    {
      id: 'scanner-section',
      kind: 'fieldGroup',
      title: 'Scanner',
      rows: [
        [
          {
            key: 'scannerSystem',
            type: 'text',
            label: 'System',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'Manufacturer',
              },
            },
          },
        ],
        [
          {
            key: 'scannerFieldStrength',
            type: 'text',
            label: 'Field Strength',
            defaultValue: '',
            isEditable: true,
            isRequired: true,
            isDisabled: false,
            relationship: {
              kind: 'sourced',
              isAutomatic: true,
              source: {
                kind: 'dicom',
                sourceKey: 'MagneticFieldStrength',
              },
            },
          },
        ],
      ],
    },
    {
      id: 'additional-notes',
      kind: 'freeText',
      title: '',
      text: 'The BE-OS CMR Report provides an analysis on myocardial tissue characteristics derived from MRI data. The method has been extensively validated and is pending regulatory approval. A trained clinician is required to interpret the results of this analysis in conjunction with other diagnostic tests and the client\'s medical history for reaching a conclusion. All clinical data available should be reviewed in this context.',
    },
  ],
};

const defaultTheme: Theme = {
  colors: {
    text: '#000000', // Deep slate for excellent readability
    muted: '#64748b', // Neutral grey for helper text/placeholders
    border: '#e2e8f0', // Subtle, clean border line color
    background: '#ffffff', // Base canvas color
    accent: '#ffffff', // Clinical blue for focus states or highlights
    tableHeaderBg: '#f8fafc', // Soft off-white fill for data headers
  },
  fonts: {
    heading: {
      family: 'Helvetica',
      size: 20,
      weight: 700,
    },
    sectionLabel: {
      family: 'Helvetica',
      size: 13,
      weight: 600,
      uppercase: true,
      letterSpacing: 0.5,
    },
    body: {
      family: 'Helvetica',
      size: 12,
      weight: 400,
    },
    label: {
      family: 'Helvetica',
      size: 13,
      weight: 600,
    },
    value: {
      family: 'Helvetica',
      size: 13,
      weight: 700,
    },
  },
  spacing: {
    sectionGap: 10,
    fieldGap: 10,
    padding: 12,
  },
  borderRadius: 4,
  imagePlaceholder: {
    borderStyle: 'dashed',
    borderColor: '#cbd5e1',
    background: '#f1f5f9',
  },
  fieldVariants: {
    // For read-only summary panels or specific critical visual callouts
    highlighted: {
      background: '#f0fdf4', // Soft mint green tint
      borderRadius: 4,
      padding: 8,
    },
    // Useful for dense sidebar layouts
    compact: {
      background: '#f8fafc',
      borderRadius: 2,
      padding: 6,
    },
  },
};

export const defaultOCG19ReportTemplate: ReportTemplate = {
  id: 'ocg19-default',
  label: 'BE_OS CMR Report',
  theme: defaultTheme,
  layout: {
    size: 'a4',
    margin: 0,
    columnGap: 10,
    panels: [
      defaultSidebar,
      {
        panelId: 'main',
        width: 0.75,
        padding: { top: 20, left: 0, right: 30, bottom: 0 },
        gap: 10,
        background: '#ffffff',
        sections: [
          // 1. HeaderSection
          {
            id: 'main-header',
            kind: 'header',
            heading: 'BE-OS CMR Report',
            logoHeight: 40,
            logo: {
              url: a19ReportLogo,
              alt: 'Clinical Logo',
              align: 'right',
              width: 586,
              height: 173,
            },
          },

          // 2. RichTextSection (e.g., Clinical Summary Box)
          {
            id: 'clinical-summary',
            kind: 'richText',
            title: 'Summary',
            field: {
              key: 'summaryText',
              type: 'text',
              label: 'Findings & Interpretation',
              placeholder: 'Enter clinical summary here...',
              isEditable: true,
              isRequired: false,
            },
            cannedTextGroups: [
              {
                id: 'bmoreChoices',
                title: 'BMORE Quality:',
                isMultiSelect: true,
                choices: [
                  {
                    label: 'Normal',
                    text: 'There is a normal myocardial oxygenation response to a vasoactive breathing manoeuvre in all coronary territories.',
                  },
                  {
                    label: 'Globally Blunted',
                    text: 'There is a blunted myocardial oxygenation response to a vasoactive breathing manoeuvre involving all coronary territories.',
                  },
                  {
                    label: 'Regionally Blunted',
                    text: 'There is a blunted oxygenation response to a vasoactive breathing manoeuvre in the - territory, with a normal response in all remaining myocardial territories.',
                  },
                  {
                    label: 'Regionally Impaired',
                    text: 'Due to an artefact, the image quality is impaired in the - territory. A normal myocardial oxygenation response to a vasoactive breathing manoeuvre is observed in the remaining coronary territories.',
                  },
                  {
                    label: 'Non-diagnostic',
                    text: 'The scan is non-diagnostic; significant motion artefacts compromise reliable interpretation.',
                  },
                ],
              },
              {
                id: 'HRChoices',
                title: 'Heart Rate Response:',
                isMultiSelect: false,
                choices: [
                  {
                    label: 'High (>25)',
                    text: 'The heart rate response indicates good neuro-humoral responsiveness.',
                  },
                  {
                    label: 'Normal (15 - 25)',
                    text: 'The heart rate response indicates normal neuro-humoral responsiveness.',
                  },
                  {
                    label: 'Low (<15)',
                    text: 'The heart rate response indicates low neuro-humoral responsiveness.',
                  },
                ],
              },
              {
                id: 'optionalChoices',
                title: 'Optional:',
                isMultiSelect: true,
                choices: [
                  {
                    label: 'Reduced Diagnostic Confidence',
                    text: 'Impaired image quality due to motion artefacts early in the breath-hold, limiting reliable interpretation. Within this limitation, there appears to be a normal myocardial oxygenation response to a vasoactive breathing manoeuvre in all coronary territories.',
                  },
                ],
              },
            ],
          },

          // 3. StatRowSection (e.g., Heart Rate Baseline metrics)
          {
            id: 'heart-rate-stats',
            kind: 'statRow',
            title: 'HEART RATE RESPONSE',
            unit: 'bpm',
            stats: [
              {
                key: 'heartRate.baseline',
                label: 'Baseline (min)',
                type: 'number',
                isEditable: true,
              },
              {
                key: 'heartRate.peak',
                label: 'Hyperventilation (max)',
                type: 'number',
                isEditable: true,
              },
              {
                key: 'heartRate.delta',
                label: 'Delta (max - min)',
                type: 'number',
                isEditable: false,
                isDisabled: true,
                relationship: {
                  kind: 'derived',
                  parentKeys: ['heartRate.peak', 'heartRate.baseline'],
                  action: 'difference',
                  isAutomatic: true,
                },
              },
            ],
          },

          // 4. TableSection
          {
            id: 'biomarker-table',
            kind: 'table',
            title: 'BIOMARKERS',
            columns: ['Biomarker', 'Global', 'LAD', 'RCA', 'LCX', 'Normal*'],
            rows: [
              {
                id: 'row-bmore-diastolic',
                label: 'B-MORE Diastole',
                cells: [
                  {
                    key: 'table.bmore.diastolic.global',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgDiaMaxIntensity',
                        sourceKey: 'Global',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.diastolic.lad',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgDiaMaxIntensity',
                        sourceKey: 'LAD',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.diastolic.rca',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgDiaMaxIntensity',
                        sourceKey: 'RCA',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.diastolic.lcx',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgDiaMaxIntensity',
                        sourceKey: 'LCx',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.diastolic.normal',
                    type: 'text',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgDiaMaxIntensity',
                        sourceKey: 'Normal',
                      },
                    },
                  },
                ],
              },
              {
                id: 'row-bmore-micro',
                label: 'B-MORE Micro',
                cells: [
                  {
                    key: 'table.bmore.micro.global',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgEntropy',
                        sourceKey: 'Global',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.micro.lad',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgEntropy',
                        sourceKey: 'LAD',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.micro.rca',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgEntropy',
                        sourceKey: 'RCA',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.micro.lcx',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgEntropy',
                        sourceKey: 'LCx',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.micro.normal',
                    type: 'text',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'ocgEntropy',
                        sourceKey: 'Normal',
                      },
                    },
                  },
                ],
              },
              {
                id: 'row-bmore-max',
                label: 'B-MORE Max',
                cells: [
                  {
                    key: 'table.bmore.max.global',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMOREMax',
                        sourceKey: 'Global',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.max.lad',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMOREMax',
                        sourceKey: 'LAD',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.max.rca',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMOREMax',
                        sourceKey: 'RCA',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.max.lcx',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMOREMax',
                        sourceKey: 'LCx',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.max.normal',
                    type: 'text',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMOREMax',
                        sourceKey: 'Normal',
                      },
                    },
                  },
                ],
              },

              {
                id: 'row-bmore',
                label: 'B-MORE',
                cells: [
                  {
                    key: 'table.bmore.global',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMORE',
                        sourceKey: 'Global',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.lad',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMORE',
                        sourceKey: 'LAD',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.rca',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMORE',
                        sourceKey: 'RCA',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.lcx',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMORE',
                        sourceKey: 'LCx',
                      },
                    },
                  },
                  {
                    key: 'table.bmore.normal',
                    type: 'text',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'analysis',
                        pipelineId: 'BMORE',
                        sourceKey: 'Normal',
                      },
                    },
                  },
                ],
              },
            ],
            footnote:
              '*Normal values are subject to change as we continue validating our biomarkers against larger datasets.',
          },

          // 5. ImageGridSection (2 Columns - Set 1)
          {
            id: 'imaging-grid-1',
            kind: 'imageGrid',
            title: 'OXYCARDIOGRAM ANALYSIS OUTPUT',
            columns: 2,
            imageHeight: 175,
            cells: [
              {
                id: 'image-cell-1',
                titleField: {
                  key: 'image.cell-1.title',
                  type: 'text',
                },
                imageField: {
                  key: 'image.cell-1.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
              {
                id: 'image-cell-2',
                titleField: {
                  key: 'image.cell-2.title',
                  type: 'text',
                },
                imageField: {
                  key: 'image.cell-2.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
            ],
          },

          // 6. ImageGridSection (2 Columns - Set 2)
          {
            id: 'imaging-grid-2',
            kind: 'imageGrid',
            title: '',
            columns: 2,
            imageHeight: 175,
            cells: [
              {
                id: 'image-cell-3',
                titleField: {
                  key: 'image-cell-3.title',
                  type: 'text',
                },
                imageField: {
                  key: 'image-cell-3.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
              {
                id: 'image-cell-4',
                titleField: {
                  key: 'image-cell-4.title',
                  type: 'text',
                },
                imageField: {
                  key: 'image-cell-4.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
            ],
          },
        ],
      },
    ],
  },
};

const defaultFunctionReportTemplate: ReportTemplate = {
  id: 'ocg19-function-full',
  label: 'Function Report (All Views)',
  theme: defaultTheme,
  layout: {
    size: 'a4',
    margin: 0,
    columnGap: 10,
    panels: [
      defaultSidebar,
      {
        panelId: 'main',
        width: 0.75,
        padding: { top: 20, left: 0, right: 30, bottom: 0 },
        gap: 10,
        background: '#ffffff',
        sections: [
          {
            id: 'function-header',
            kind: 'header',
            heading: 'Function Report',
            logoHeight: 40,
            logo: {
              url: a19ReportLogo,
              alt: 'Clinical Logo',
              align: 'right',
              width: 586,
              height: 173,
            },
          },
          {
            id: 'function-summary',
            kind: 'richText',
            title: 'Summary',
            field: {
              key: 'function.summaryText',
              type: 'text',
              label: 'Findings & Interpretation',
              placeholder: 'Enter function report summary here...',
              isEditable: true,
              isRequired: false,
            },
          },
          {
            id: 'function-LV-volume-table',
            kind: 'table',
            title: 'LV Volume Analysis',
            columns: ['View', 'EDV (ml)', 'ESV (ml)', 'SV (ml)', 'EF (%)'],
            rows: [
              {
                id: 'row-function-sax-LV-volume-table',
                label: 'SAX',
                cells: [
                  {
                    key: 'table.function.sax.LV-volume.edv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'EDV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.LV-volume.esv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'ESV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.LV-volume.sv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'SV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.LV-volume.ef',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'EF',
                      },
                    },
                  },
                ],
              },
              {
                id: 'row-function-lax2c-LV-volume-table',
                label: 'LAX Monoplane (2CV)',
                cells: [
                  {
                    key: 'table.function.lax2c.LV-volume.edv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax2c.LV-volume.esv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax2c.LV-volume.sv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax2c.LV-volume.ef',
                    type: 'number',
                    isEditable: true,
                  },
                ],
              },
              {
                id: 'row-function-lax4c-LV-volume-table',
                label: 'LAX Monoplane (4CV)',
                cells: [
                  {
                    key: 'table.function.lax4c.LV-volume.edv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax4c.LV-volume.esv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax4c.LV-volume.sv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax4c.LV-volume.ef',
                    type: 'number',
                    isEditable: true,
                  },
                ],
              },
              {
                id: 'row-function-biplane-LV-volume-table',
                label: 'LAX Biplane',
                cells: [
                  {
                    key: 'table.function.biplane.LV-volume.edv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.biplane.LV-volume.esv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.biplane.LV-volume.sv',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.biplane.LV-volume.ef',
                    type: 'number',
                    isEditable: true,
                  },
                ],
              },
            ],
          },
          {
            id: 'function-RV-volume-table',
            kind: 'table',
            title: 'RV Volume Analysis',
            columns: ['View', 'EDV (ml)', 'ESV (ml)', 'SV (ml)', 'EF (%)'],
            rows: [
              {
                id: 'row-function-sax-RV-volume-table',
                label: 'SAX',
                cells: [
                  {
                    key: 'table.function.sax.RV-volume.edv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVEDV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.RV-volume.esv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVESV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.RV-volume.sv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVSV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.RV-volume.ef',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVEF',
                      },
                    },
                  },
                ],
              },
            ],
          },

          {
            id: 'function-myo-mass-table',
            kind: 'table',
            title: 'Myocardial Mass Analysis',
            columns: ['View', 'Systolic Mass (g)', 'Diastolic Mass (g)'],
            rows: [
              {
                id: 'row-function-sax-myo-mass-table',
                label: 'SAX',
                cells: [
                  {
                    key: 'table.function.sax.myo-mass.systolic',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'systolicMyoMass',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.myo-mass.diastolic',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'diastolicMyoMass',
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            id: 'function-GLS-table',
            kind: 'table',
            title: 'Global longitudinal strain (GLS)',
            columns: ['View', 'GLS Endo (%)', 'GLS Epi (%)'],
            rows: [
              {
                id: 'row-function-lax2c-GLS-table',
                label: 'LAX Monoplane (2CV)',
                cells: [
                  {
                    key: 'table.function.lax2c.GLS.endo',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax2c.GLS.epi',
                    type: 'number',
                    isEditable: true,
                  },
                ],
              },
              {
                id: 'row-function-lax4c-GLS-table',
                label: 'LAX Monoplane (4CV)',
                cells: [
                  {
                    key: 'table.function.lax4c.GLS.endo',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.lax4c.GLS.epi',
                    type: 'number',
                    isEditable: true,
                  },
                ],
              },
              {
                id: 'row-function-biplane-GLS-table',
                label: 'LAX Biplane',
                cells: [
                  {
                    key: 'table.function.biplane.GLS.endo',
                    type: 'number',
                    isEditable: true,
                  },
                  {
                    key: 'table.function.biplane.GLS.epi',
                    type: 'number',
                    isEditable: true,
                  },
                ],
              },
            ],
          },
          // 5. ImageGridSection (2 Columns - Set 1)
          {
            id: 'imaging-grid-function',
            kind: 'imageGrid',
            title: '',
            columns: 2,
            imageHeight: 175,
            cells: [
              {
                id: 'function-image-cell-1',
                titleField: {
                  key: 'function.image.cell-1.title',
                  type: 'text',
                },
                imageField: {
                  key: 'function.image.cell-1.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
              {
                id: 'function-image-cell-2',
                titleField: {
                  key: 'function.image.cell-2.title',
                  type: 'text',
                },
                imageField: {
                  key: 'function.image.cell-2.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
            ],
          },
        ],
      },
    ],
  },
};

const functionReportTemplateSAX: ReportTemplate = {
  id: 'ocg19-function-sax',
  label: 'Function Report (Sax)',
  theme: defaultTheme,
  layout: {
    size: 'a4',
    margin: 0,
    columnGap: 10,
    panels: [
      defaultSidebar,
      {
        panelId: 'main',
        width: 0.75,
        padding: { top: 20, left: 0, right: 30, bottom: 0 },
        gap: 10,
        background: '#ffffff',
        sections: [
          {
            id: 'function-header',
            kind: 'header',
            heading: 'Function Report',
            logoHeight: 40,
            logo: {
              url: a19ReportLogo,
              alt: 'Clinical Logo',
              align: 'right',
              width: 586,
              height: 173,
            },
          },
          {
            id: 'function-summary',
            kind: 'richText',
            title: 'Summary',
            field: {
              key: 'function.summaryText',
              type: 'text',
              label: 'Findings & Interpretation',
              placeholder: 'Enter function report summary here...',
              isEditable: true,
              isRequired: false,
            },
          },
          {
            id: 'function-LV-volume-table',
            kind: 'table',
            title: 'LV Volume Analysis',
            columns: ['View', 'EDV (ml)', 'ESV (ml)', 'SV (ml)', 'EF (%)'],
            rows: [
              {
                id: 'row-function-sax-LV-volume-table',
                label: 'SAX',
                cells: [
                  {
                    key: 'table.function.sax.LV-volume.edv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'EDV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.LV-volume.esv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'ESV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.LV-volume.sv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'SV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.LV-volume.ef',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'EF',
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            id: 'function-RV-volume-table',
            kind: 'table',
            title: 'RV Volume Analysis',
            columns: ['View', 'EDV (ml)', 'ESV (ml)', 'SV (ml)', 'EF (%)'],
            rows: [
              {
                id: 'row-function-sax-RV-volume-table',
                label: 'SAX',
                cells: [
                  {
                    key: 'table.function.sax.RV-volume.edv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVEDV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.RV-volume.esv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVESV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.RV-volume.sv',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVSV',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.RV-volume.ef',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'RVEF',
                      },
                    },
                  },
                ],
              },
            ],
          },

          {
            id: 'function-myo-mass-table',
            kind: 'table',
            title: 'Myocardial Mass Analysis',
            columns: ['View', 'Systolic Mass (g)', 'Diastolic Mass (g)'],
            rows: [
              {
                id: 'row-function-sax-myo-mass-table',
                label: 'SAX',
                cells: [
                  {
                    key: 'table.function.sax.myo-mass.systolic',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'systolicMyoMass',
                      },
                    },
                  },
                  {
                    key: 'table.function.sax.myo-mass.diastolic',
                    type: 'number',
                    isEditable: true,
                    relationship: {
                      kind: 'sourced',
                      isAutomatic: true,
                      source: {
                        kind: 'function-table',
                        axis: 'SaxStack',
                        sourceKey: 'diastolicMyoMass',
                      },
                    },
                  },
                ],
              },
            ],
          },
          // 5. ImageGridSection (2 Columns - Set 1)
          {
            id: 'imaging-grid-function',
            kind: 'imageGrid',
            title: '',
            columns: 2,
            imageHeight: 175,
            cells: [
              {
                id: 'function-image-cell-1',
                titleField: {
                  key: 'function.image.cell-1.title',
                  type: 'text',
                },
                imageField: {
                  key: 'function.image.cell-1.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
              {
                id: 'function-image-cell-2',
                titleField: {
                  key: 'function.image.cell-2.title',
                  type: 'text',
                },
                imageField: {
                  key: 'function.image.cell-2.image',
                  type: 'image',
                  defaultValue: null,
                },
              },
            ],
          },
        ],
      },
    ],
  },
};

// ─── Template Registry ──────────────────────────────────────────────────────

export type ReportTemplateId
  = | 'ocg19-default'
    | 'ocg19-function-full'
    | 'ocg19-function-sax';

export const availableTemplates: ReportTemplateId[] = [
  'ocg19-default',
  'ocg19-function-full',
  'ocg19-function-sax',
];

export const templateIdToTemplateMap: Record<ReportTemplateId, ReportTemplate>
  = {
    'ocg19-default': defaultOCG19ReportTemplate,
    'ocg19-function-full': defaultFunctionReportTemplate,
    'ocg19-function-sax': functionReportTemplateSAX,
  };

export function getTitleForTemplateId(templateId: ReportTemplateId): string {
  const template = templateIdToTemplateMap[templateId];
  return template ? template.label : 'Unknown Template';
}

export function getTemplateById(
  templateId: ReportTemplateId,
): ReportTemplate | null {
  return templateIdToTemplateMap[templateId] || null;
}

export function getDefaultTemplate(): ReportTemplate {
  return defaultOCG19ReportTemplate;
}
export function getDefaultTemplateId(): ReportTemplateId {
  return 'ocg19-default';
}

export function getIDFromTemplate(
  template: ReportTemplate,
): ReportTemplateId | null {
  const entry = Object.entries(templateIdToTemplateMap).find(
    ([, value]) => value.id === template.id,
  );
  return entry ? (entry[0] as ReportTemplateId) : null;
}

export function getDropdownOptionsForKey(key: string): FieldOption[] | null {
  return defaultSelections[key] || null;
}

/**
 * Returns all available templates as { id, label } entries.
 * Useful for the "Manage Pages" dropdown in ReportMaker.
 */
export function getAllTemplateEntries(): {
  id: ReportTemplateId;
  label: string;
}[] {
  return availableTemplates.map(tid => ({
    id: tid,
    label: templateIdToTemplateMap[tid].label,
  }));
}
